import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, Loader2 } from 'lucide-react'
import { AbilityType } from 'app/frontend/types/custom/Abilities'
import RaidPlan, {
  RaidPlanActor,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import Select from 'app/frontend/base/components/Select'
import TeamPlanTimeline from './TeamPlanTimeline'
import { TeamPlanTimelineProvider } from './TeamPlanTimelineContext'
import useTeamPlanComputedData from '../hooks/useTeamPlanComputedData'
import { ResolvedAssignmentParticipant } from '../../TeamEvent/components/eventActivityTimelineResolver'
import Card from 'app/frontend/base/components/Card'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { Button } from '@/components/ui/button'

const LOCAL_REPLACEMENT_UUID = 'replacement-actor'

const cloneTimeline = (timeline: RaidPlanTimeline): RaidPlanTimeline => ({
  ...timeline,
  phase_durations: timeline.phase_durations
    ? { ...timeline.phase_durations }
    : undefined,
  actors: timeline.actors.map((actor) => ({
    ...actor,
    casts: actor.casts.map((cast) => ({ ...cast })),
  })),
  general: timeline.general
    ? {
        ...timeline.general,
        casts: timeline.general.casts.map((cast) => ({ ...cast })),
      }
    : undefined,
  encounter_assignments: timeline.encounter_assignments
    ? {
        ...timeline.encounter_assignments,
        ability_order: timeline.encounter_assignments.ability_order
          ? [...timeline.encounter_assignments.ability_order]
          : undefined,
        abilities: Object.entries(
          timeline.encounter_assignments.abilities,
        ).reduce<
          Record<
            RaidPlanEncounterAssignmentAbilityKey,
            RaidPlanEncounterAssignmentAbility
          >
        >((acc, [key, ability]) => {
          acc[key] = {
            ...ability,
            casts: ability.casts.map((cast) => ({
              ...cast,
              boxes: cast.boxes.map((box) => ({
                ...box,
                entities: box.entities.map((entity) => ({ ...entity })),
              })),
            })),
          }
          return acc
        }, {}),
      }
    : undefined,
})

const buildDialogTimeline = ({
  raidPlan,
  originalActor,
  replacementActor,
}: {
  raidPlan: RaidPlan
  originalActor: RaidPlanActor
  replacementActor?: RaidPlanActor
}): RaidPlanTimeline => {
  const nextTimeline = cloneTimeline(raidPlan.timeline)
  nextTimeline.actors = [
    {
      ...originalActor,
      casts: originalActor.casts.map((cast) => ({ ...cast })),
    },
    ...(replacementActor
      ? [
          {
            ...replacementActor,
            uuid: LOCAL_REPLACEMENT_UUID,
            casts: replacementActor.casts.map((cast) => ({ ...cast })),
          },
        ]
      : []),
  ]
  return nextTimeline
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  planListPathPrefix: string
  rosterMemberships: wowaudit.TeamMembership[]
  raidPlan: RaidPlan
  originalActor: RaidPlanActor
  replacementActor?: RaidPlanActor
  isIgnored: boolean
  isSaving: boolean
  participants: ResolvedAssignmentParticipant[]
  onReplacementActorChange: (
    replacementActor: RaidPlanActor | undefined,
  ) => void
  onIgnoredChange: (ignored: boolean) => void
}

const UnresolvedActorReplacementDialog = ({
  open,
  onOpenChange,
  planListPathPrefix,
  rosterMemberships,
  raidPlan,
  originalActor,
  replacementActor,
  isIgnored,
  isSaving,
  participants,
  onReplacementActorChange,
  onIgnoredChange,
}: Props) => {
  const { t } = useTranslation()
  const [selectedType, setSelectedType] = useState<AbilityType>('key_cooldown')
  const [zoom, setZoom] = useState<{ start: number; end: number } | null>(null)
  const timelineRootRef = useRef<HTMLDivElement>(null)
  const [timeline, setTimeline] = useState<RaidPlanTimeline>(() =>
    buildDialogTimeline({
      raidPlan,
      originalActor,
      replacementActor,
    }),
  )

  useEffect(() => {
    if (!open) return
    setTimeline(
      buildDialogTimeline({
        raidPlan,
        originalActor,
        replacementActor,
      }),
    )
    setZoom(null)
  }, [open, originalActor, raidPlan, replacementActor])

  const computed = useTeamPlanComputedData({
    raidPlan,
    timeline,
    selectedType,
    t,
  })

  const persistReplacementActor = useCallback(
    (nextTimeline: RaidPlanTimeline) => {
      const nextReplacementActor = nextTimeline.actors[1]
      if (!nextReplacementActor) {
        onReplacementActorChange(undefined)
        return
      }
      onReplacementActorChange({
        ...nextReplacementActor,
        uuid: originalActor.uuid,
        casts: nextReplacementActor.casts.map((cast) => ({ ...cast })),
      })
    },
    [onReplacementActorChange, originalActor.uuid],
  )

  const updateTimeline = useCallback(
    (updater: (next: RaidPlanTimeline) => void, _pushHistory = true) => {
      setTimeline((previousTimeline) => {
        const nextTimeline = cloneTimeline(previousTimeline)
        updater(nextTimeline)
        persistReplacementActor(nextTimeline)
        return nextTimeline
      })
    },
    [persistReplacementActor],
  )

  const participantOptions = useMemo(
    () =>
      participants
        .map((participant) => ({
          label: '',
          customOptionFn: () => (
            <span className='flex items-center gap-2'>
              <CharacterClassIcon
                characterClass={participant.characterClass}
                size='small'
              />
              <span className={classColours[participant.characterClass].text}>
                {participant.name}
              </span>
            </span>
          ),
          sortLabel: participant.name,
          value: participant.characterId.toString(),
        }))
        .sort((left, right) =>
          (left.sortLabel ?? '').localeCompare(right.sortLabel ?? ''),
        ),
    [participants],
  )

  const hasReplacementActor = timeline.actors.length > 1
  const actorRowBehaviorByUuid = useMemo(() => {
    const behavior: Record<
      string,
      {
        showPreferredCharacter?: boolean
        allowRemove?: boolean
        allowSpecializationEdit?: boolean
        allowCastEdit?: boolean
      }
    > = {
      [originalActor.uuid]: {
        showPreferredCharacter: false,
        allowRemove: false,
        allowSpecializationEdit: false,
        allowCastEdit: false,
      },
    }
    if (hasReplacementActor) {
      behavior[LOCAL_REPLACEMENT_UUID] = {
        showPreferredCharacter: false,
        allowRemove: true,
        allowSpecializationEdit: false,
        allowCastEdit: true,
      }
    }
    return behavior
  }, [hasReplacementActor, originalActor.uuid])

  const timelineContextValue = useMemo(
    () => ({
      canEdit: true,
      owner:
        raidPlan.ownerType === 'User'
          ? ({ ownerType: 'user' } as const)
          : ({
              ownerType: 'team',
              ownerId: raidPlan.ownerId,
            } as const),
      basePath:
        raidPlan.ownerType === 'User'
          ? `${planListPathPrefix}/plans`
          : `${planListPathPrefix}/events/plans`,
      planListPathPrefix,
      rosterMemberships,
      raidPlan,
      timeline,
      isInitializing: false,
      selectedType,
      setSelectedType,
      zoom,
      setZoom,
      setDragFrameMax: () => undefined,
      computed,
      timelineRootRef,
      viewers: [],
      cursors: [],
      showRosterImport: false,
      cursorContext: `tab:${selectedType}`,
      setAssignmentDialogOpen: () => null,
      sendCursor: () => null,
      updateTimeline,
      applyPlanFromSource: () => undefined,
    }),
    [
      computed,
      planListPathPrefix,
      raidPlan,
      rosterMemberships,
      selectedType,
      timeline,
      updateTimeline,
      zoom,
    ],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[90vw] w-[1200px]'>
        <DialogHeader>
          <DialogTitle>Replace cooldowns</DialogTitle>
        </DialogHeader>
        <TeamPlanTimelineProvider value={timelineContextValue}>
          <TeamPlanTimeline
            inFocusedDialog
            actorRowBehaviorByUuid={actorRowBehaviorByUuid}
            beforeActorContentByUuid={
              hasReplacementActor
                ? {
                    [LOCAL_REPLACEMENT_UUID]: (
                      <div className='flex justify-center py-2'>
                        <ArrowDown className='h-8 w-8 text-muted-foreground' />
                      </div>
                    ),
                  }
                : undefined
            }
            extraActorContent={
              hasReplacementActor ? null : (
                <div>
                  <div className='flex justify-center py-2'>
                    <ArrowDown className='h-8 w-8 text-muted-foreground' />
                  </div>
                  <Card className='border-dashed flex items-center gap-2 justify-center'>
                    <div className='text-sm font-medium'>Replace with</div>
                    <Select
                      inModal
                      options={participantOptions}
                      value={undefined}
                      disabled={isSaving}
                      setValue={(value) => {
                        if (!value) return
                        const participant = participants.find(
                          (entry) => entry.characterId.toString() === value,
                        )
                        if (!participant) return
                        onIgnoredChange(false)
                        updateTimeline((next) => {
                          const replacement: RaidPlanActor = {
                            uuid: LOCAL_REPLACEMENT_UUID,
                            spec_id: participant.specId,
                            class_id: participant.classId as number,
                            preferred_character_id: participant.characterId,
                            label: participant.name,
                            casts: [],
                          }
                          next.actors = [next.actors[0], replacement]
                        })
                      }}
                      placeholder='Select character'
                      className='w-fit'
                    />
                    {isIgnored ? null : (
                      <>
                        <div className='text-sm font-medium mx-1'>or</div>
                        <Button
                          variant={'outline'}
                          className='w-20'
                          disabled={isSaving}
                          onClick={() => onIgnoredChange(true)}
                        >
                          {isSaving ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            'Ignore'
                          )}
                        </Button>
                      </>
                    )}
                  </Card>
                </div>
              )
            }
          />
        </TeamPlanTimelineProvider>

        <div className='justify-end flex gap-2'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UnresolvedActorReplacementDialog
